import React, { useState } from "react";
// removed useNavigate import (unused after removing bulk upload button)
import Card from "components/card";
import InputField from "components/fields/InputField";
import { Toaster, toast } from "react-hot-toast";

const Returns = () => {
  // navigate removed (bulk upload button removed)
  const [formData, setFormData] = useState({
    date: "",
    rcn_type: "",
    moisture: "",
    origin: "",
    outturn: "",
    size: "",
    quantity: "",
    zone: "",
    real: "",
    number_of_bags: ""
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;
    const newFormData = {
      ...formData,
      [id]: value
    };

    // Calculate Real value when Quantity or Number of Bags changes
    if (id === 'quantity' || id === 'number_of_bags') {
      const quantity = id === 'quantity' ? (value ? parseFloat(value) : 0) : (formData.quantity ? parseFloat(formData.quantity) : 0);
      const bags = id === 'number_of_bags' ? (value ? parseInt(value) : 0) : (formData.number_of_bags ? parseInt(formData.number_of_bags) : 0);
      if (quantity > 0 || bags > 0) {
        const bagsWeight = bags * 3; // First calculate bags × 3kg
        const realValue = quantity - bagsWeight; // Then subtract from quantity
        newFormData.real = realValue.toFixed(2);
      } else {
        newFormData.real = "";
      }
    }

    // Format size value with prefix
    if (id === 'size' && value) {
      // keep size value as selected (no prefix) for returns API
      newFormData.size = value;
    }

    setFormData(newFormData);
  };

  const showAlert = (message, type) => {
    if (type === 'success') {
      toast.success(message, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });
    } else {
      toast.error(message, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.rcn_type) newErrors.rcn_type = "RCN Type is required";
    if (!formData.number_of_bags) newErrors.number_of_bags = "Number of Bags is required";
    if (!formData.quantity) newErrors.quantity = "Quantity Calibrated is required";
    if (!formData.size) newErrors.size = "Size is required";
    if (!formData.zone) newErrors.zone = "Zone is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowVerificationModal(true);
    } else {
      showAlert('Please check the form for errors', 'error');
    }
  };

  const handleVerifiedSubmit = async () => {
    setIsLoading(true);
    setShowVerificationModal(false);
    
    try {
      const transformedData = {
        date: formData.date || null,
        rcn_type: formData.rcn_type || null,
        moisture: formData.moisture ? parseFloat(formData.moisture) : null,
        origin: formData.origin || null,
        outturn: formData.outturn ? parseFloat(formData.outturn) : null,
        size: formData.size || null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        zone: formData.zone || null,
        real_weight: formData.real ? parseFloat(formData.real) : null,
        number_of_bags: formData.number_of_bags ? parseInt(formData.number_of_bags) : null
      };

      const response = await fetch("https://usibras-web-backend.onrender.com/api/rcn-returns", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // sample response: { id: 1, message: 'Record added', success: true }
      showAlert(data.message || 'Return data successfully added.', 'success');
      
      // Reset form
      setFormData({
        date: "",
        rcn_type: "",
        moisture: "",
        origin: "",
        outturn: "",
        size: "",
        quantity: "",
        zone: "",
        real: "",
        number_of_bags: ""
      });
    } catch (error) {
      console.error('Error details:', error);
      showAlert(error.message || "An error occurred", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <Toaster />
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Add Returned Data
          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in the return information
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Basic Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Date *"
                id="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                error={errors.date}
                required
              />
              <div className="flex flex-col">
                <label className="text-sm text-navy-700 font-medium mb-1">RCN Type *</label>
                <select
                  className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                  id="rcn_type"
                  value={formData.rcn_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select RCN type</option>
                  <option value="Fair Trade">Fair Trade</option>
                  <option value="Organic">Organic</option>
                  <option value="Conventional">Conventional</option>
                </select>
                {errors.rcn_type && (
                  <p className="text-red-500 text-xs mt-1">{errors.rcn_type}</p>
                )}
              </div>
              <InputField
                label="Origin"
                id="origin"
                type="text"
                value={formData.origin}
                onChange={handleChange}
                error={errors.origin}
              />
              <InputField
                label="Number of Bags *"
                id="number_of_bags"
                type="number"
                value={formData.number_of_bags}
                onChange={handleChange}
                error={errors.number_of_bags}
                required
              />
            </div>
          </div>

          {/* Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Moisture"
                id="moisture"
                type="number"
                step="0.01"
                value={formData.moisture}
                onChange={handleChange}
                error={errors.moisture}
              />
              <InputField
                label="Outturn"
                id="outturn"
                type="number"
                step="0.01"
                value={formData.outturn}
                onChange={handleChange}
                error={errors.outturn}
              />
              <InputField
                label="Quantity Calibrated  (KG) *"
                id="quantity"
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={handleChange}
                error={errors.quantity}
                required
              />
              <InputField
                label="Real Quantity Calibrated (KG)"
                id="real"
                type="number"
                step="0.01"
                value={formData.real}
                readOnly
                className="bg-gray-100"
                error={errors.real}
              />
            </div>
          </div>

          {/* Size and Zone Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Size and Zone Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col">
                <label className="text-sm text-navy-700 font-medium mb-1">Size *</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      id="size"
                      value={formData.size || ''}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select size</option>
                      {[15, 17, 19, 21, 23, 25, 28].map((s) => (
                        <React.Fragment key={`size-${s}`}>
                          <option value={`+${s}`}>+{s}</option>
                          <option value={`-${s}`}>-{s}</option>
                        </React.Fragment>
                      ))}
                    </select>
                {errors.size && (
                  <p className="text-red-500 text-xs mt-1">{errors.size}</p>
                    )}
              </div>
                  <div className="flex flex-col">
                <label className="text-sm text-navy-700 font-medium mb-1">Zone *</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                  id="zone"
                  value={formData.zone}
                  onChange={handleChange}
                      required
                >
                  <option value="">Select zone</option>
                  {[...Array(16)].map((_, i) => (
                    <option key={`R${i + 1}`} value={`R${i + 1}`}>R{i + 1}</option>
                  ))}
                  {[...Array(16)].map((_, i) => (
                    <option key={`L${i + 1}`} value={`L${i + 1}`}>L{i + 1}</option>
                      ))}
                    </select>
                {errors.zone && (
                  <p className="text-red-500 text-xs mt-1">{errors.zone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setFormData({
                date: "",
                rcn_type: "",
                moisture: "",
                origin: "",
                outturn: "",
                size: "",
                quantity: "",
                zone: "",
                real: "",
                number_of_bags: ""
              })}
              className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 flex items-center justify-center min-w-[120px]"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </Card>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl w-[800px] max-h-[80vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-navy-700 mb-6">
              Verify Return Data
            </h3>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-navy-700 mb-3">Return Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">{formData.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">RCN Type</p>
                    <p className="font-medium">{formData.rcn_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Moisture</p>
                    <p className="font-medium">{formData.moisture}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Origin</p>
                    <p className="font-medium">{formData.origin}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Outturn</p>
                    <p className="font-medium">{formData.outturn}</p>
              </div>
                  <div>
                    <p className="text-sm text-gray-600">Size</p>
                    <p className="font-medium">{formData.size}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Quantity</p>
                    <p className="font-medium">{formData.quantity} KG</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Zone</p>
                    <p className="font-medium">{formData.zone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Real</p>
                    <p className="font-medium">{formData.real} KG</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Number of Bags</p>
                    <p className="font-medium">{formData.number_of_bags}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                type="button"
                onClick={() => setShowVerificationModal(false)}
                className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifiedSubmit}
                className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600"
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Returns;
